import { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Select, Button, message, Card, Switch, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { getPlanDetails, updatePlan } from './PlansapiFunction';

const { TextArea } = Input;
const { Option } = Select;

const EditPlan = ({ planId, onBack }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await getPlanDetails(planId?.id);
        if (response.status === 'success') {
          const plan = response.data;
          form.setFieldsValue({
            ...plan,
            is_active: plan.is_active === '1',
            price: parseFloat(plan.price),
            features: plan.features || []
          });
        }
      } catch (error) {
        message.error('Failed to fetch plan details');
      }
      setFetchLoading(false);
    };
    fetchPlan();
  }, [planId, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        is_active: values.is_active ? '1' : '0'
      };
      await updatePlan(planId, payload);
      message.success('Plan updated successfully!');
      setTimeout(onBack, 1000);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update plan');
    }
    setLoading(false);
  };

  if (fetchLoading) {
    return (
      <div className="text-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={onBack}
        className="mb-4"
      >
        Back to Plans
      </Button>

      <Card title="Edit Plan" className="shadow-sm">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div>
              <Form.Item
                name="name"
                label="Plan Name"
                rules={[{ required: true, message: 'Please enter plan name' }]}
              >
                <Input placeholder="Plan name" size="large" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: 'Please enter description' }]}
              >
                <TextArea rows={3} placeholder="Plan description" />
              </Form.Item>

              <Form.Item
                name="plan_type"
                label="Plan Type"
                rules={[{ required: true }]}
              >
                <Select size="large" disabled>
                  <Option value="free">Free</Option>
                  <Option value="paid">Paid</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="price"
                label="Price (₹)"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <InputNumber 
                  className="w-full" 
                  size="large"
                  min={0}
                  step={0.01}
                  prefix="₹"
                />
              </Form.Item>

              <Form.Item
                name="duration_days"
                label="Duration (Days)"
                rules={[{ required: true, message: 'Please enter duration' }]}
              >
                <InputNumber 
                  className="w-full" 
                  size="large"
                  min={1}
                />
              </Form.Item>
            </div>

            {/* Right Column */}
            <div>
              <Form.Item
                name="max_restaurants"
                label="Max Restaurants"
              >
                <InputNumber 
                  className="w-full" 
                  size="large"
                  min={0}
                  placeholder="Leave empty for unlimited"
                />
              </Form.Item>

              <Form.Item
                name="max_tables_per_restaurant"
                label="Max Tables Per Restaurant"
              >
                <InputNumber 
                  className="w-full" 
                  size="large"
                  min={0}
                  placeholder="Leave empty for unlimited"
                />
              </Form.Item>

              <Form.Item
                name="features"
                label="Features"
              >
                <Select
                  mode="tags"
                  size="large"
                  placeholder="Add features (press Enter)"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="is_active"
                label="Active Status"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="Active" 
                  unCheckedChildren="Inactive"
                />
              </Form.Item>

              <div className="flex gap-3 mt-4">
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                  className="w-full"
                >
                  Update Plan
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default EditPlan;